import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft, Shield, Clock, CreditCard, AlertCircle, CheckCircle, Phone, Mail } from 'lucide-react';

export default function RefundPolicyPage() {
  const [isScrolled, setIsScrolled] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  return (
    <div className="min-h-screen bg-gradient-to-b from-slate-50 to-white">
      {/* Header Navigation */}
      <nav className={`fixed top-0 w-full z-50 transition-all duration-300 ${
        isScrolled 
          ? 'bg-white/80 backdrop-blur-lg border-b border-gray-200 shadow-md' 
          : 'bg-sky-100 backdrop-blur-md border-b border-sky-200 shadow-sm'
      }`}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-20">
            <Link to="/" className="flex items-center gap-3">
              <img 
                src="https://customer-assets.emergentagent.com/job_07322645-a840-4848-8247-f88636798b30/artifacts/kyqhtx8f_MCH%20Logo-2.png" 
                alt="My Corporate Hub" 
                className="h-16 w-auto"
              />
            </Link>
            <Link to="/" className="flex items-center gap-2 text-navy hover:text-gold transition-colors">
              <ArrowLeft className="w-4 h-4" />
              Back to Home
            </Link>
          </div>
        </div>
      </nav>

      {/* Hero Section */}
      <section className="pt-32 pb-12 bg-gradient-to-r from-navy-900 to-navy-800">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <Shield className="w-16 h-16 text-gold mx-auto mb-6" />
          <h1 className="text-4xl md:text-5xl font-bold text-white mb-4">
            Refund & Cancellation Policy
          </h1>
          <p className="text-xl text-slate-300">
            Transparent policies for your peace of mind
          </p>
          <p className="text-sm text-slate-700 mt-4">
            Last Updated: December 2025
          </p>
        </div>
      </section>

      {/* Policy Content */}
      <section className="py-16">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          
          {/* Quick Overview */}
          <div className="grid md:grid-cols-3 gap-6 mb-12">
            <div className="bg-white p-6 rounded-xl border border-gray-200 shadow-sm">
              <Clock className="w-10 h-10 text-gold mb-4" />
              <h3 className="font-bold text-navy mb-2">Refund Timeline</h3>
              <p className="text-sm text-slate-800">5-7 business days for approved refunds</p>
            </div>
            <div className="bg-white p-6 rounded-xl border border-gray-200 shadow-sm">
              <CreditCard className="w-10 h-10 text-gold mb-4" />
              <h3 className="font-bold text-navy mb-2">Payment Method</h3>
              <p className="text-sm text-slate-800">Refund to original payment method</p>
            </div>
            <div className="bg-white p-6 rounded-xl border border-gray-200 shadow-sm">
              <Shield className="w-10 h-10 text-gold mb-4" />
              <h3 className="font-bold text-navy mb-2">Secure Processing</h3>
              <p className="text-sm text-slate-800">Powered by Razorpay secure gateway</p>
            </div>
          </div>

          {/* Detailed Policy */}
          <div className="bg-white rounded-2xl border border-gray-200 shadow-lg overflow-hidden">
            
            {/* Section 1: Overview */}
            <div className="p-8 border-b border-gray-100">
              <h2 className="text-2xl font-bold text-navy mb-4 flex items-center gap-3">
                <span className="w-8 h-8 bg-navy text-white rounded-full flex items-center justify-center text-sm">1</span>
                Overview
              </h2>
              <p className="text-slate-700 leading-relaxed">
                My Corporate Hub ("we", "us", "our") is committed to providing excellent service and customer satisfaction. 
                This Refund & Cancellation Policy outlines the terms and conditions for refunds and cancellations of bookings 
                and purchases made through our platform. All payments are processed securely through Razorpay Payment Gateway.
              </p>
            </div>

            {/* Section 2: Venue/Property Bookings */}
            <div className="p-8 border-b border-gray-100">
              <h2 className="text-2xl font-bold text-navy mb-4 flex items-center gap-3">
                <span className="w-8 h-8 bg-navy text-white rounded-full flex items-center justify-center text-sm">2</span>
                Venue & Property Bookings
              </h2>
              
              <div className="space-y-6">
                <div>
                  <h3 className="font-semibold text-navy mb-3">2.1 Cancellation by Customer</h3>
                  <div className="bg-slate-50 rounded-lg p-4 space-y-3">
                    <div className="flex items-start gap-3">
                      <CheckCircle className="w-5 h-5 text-green-600 mt-0.5 flex-shrink-0" />
                      <div>
                        <span className="font-medium text-slate-900">30+ days before event:</span>
                        <span className="text-slate-700"> 90% refund (10% administrative fee)</span>
                      </div>
                    </div>
                    <div className="flex items-start gap-3">
                      <CheckCircle className="w-5 h-5 text-yellow-600 mt-0.5 flex-shrink-0" />
                      <div>
                        <span className="font-medium text-slate-900">15-29 days before event:</span>
                        <span className="text-slate-700"> 50% refund</span>
                      </div>
                    </div>
                    <div className="flex items-start gap-3">
                      <CheckCircle className="w-5 h-5 text-orange-600 mt-0.5 flex-shrink-0" />
                      <div>
                        <span className="font-medium text-slate-900">7-14 days before event:</span>
                        <span className="text-slate-700"> 25% refund</span>
                      </div>
                    </div>
                    <div className="flex items-start gap-3">
                      <AlertCircle className="w-5 h-5 text-red-600 mt-0.5 flex-shrink-0" />
                      <div>
                        <span className="font-medium text-slate-900">Less than 7 days:</span>
                        <span className="text-slate-700"> No refund (booking amount forfeited)</span>
                      </div>
                    </div>
                  </div>
                </div>

                <div>
                  <h3 className="font-semibold text-navy mb-3">2.2 Cancellation by Venue/Vendor</h3>
                  <p className="text-slate-700">
                    If the venue or vendor cancels the booking, you are entitled to a <strong>100% full refund</strong> of the amount paid. 
                    We will also assist you in finding an alternative venue at no additional cost.
                  </p>
                </div>

                <div>
                  <h3 className="font-semibold text-navy mb-3">2.3 Force Majeure</h3>
                  <p className="text-slate-700">
                    In case of cancellation due to natural disasters, government restrictions, pandemics, or other unforeseen 
                    circumstances beyond our control, we will offer either a full refund or the option to reschedule the booking 
                    to a future date at no additional cost.
                  </p>
                </div>
              </div>
            </div>

            {/* Section 3: Corporate Gifts */}
            <div className="p-8 border-b border-gray-100">
              <h2 className="text-2xl font-bold text-navy mb-4 flex items-center gap-3">
                <span className="w-8 h-8 bg-navy text-white rounded-full flex items-center justify-center text-sm">3</span>
                Corporate Gifts & Merchandise
              </h2>
              
              <div className="space-y-4">
                <div className="flex items-start gap-3">
                  <CheckCircle className="w-5 h-5 text-green-600 mt-0.5 flex-shrink-0" />
                  <div>
                    <span className="font-medium text-slate-900">Before Production:</span>
                    <span className="text-slate-700"> Full refund if order is cancelled before production begins</span>
                  </div>
                </div>
                <div className="flex items-start gap-3">
                  <CheckCircle className="w-5 h-5 text-yellow-600 mt-0.5 flex-shrink-0" />
                  <div>
                    <span className="font-medium text-slate-900">During Production:</span>
                    <span className="text-slate-700"> 50% refund (to cover material and labor costs)</span>
                  </div>
                </div>
                <div className="flex items-start gap-3">
                  <AlertCircle className="w-5 h-5 text-red-600 mt-0.5 flex-shrink-0" />
                  <div>
                    <span className="font-medium text-slate-900">After Dispatch:</span>
                    <span className="text-slate-700"> No refund for customized items. Standard items can be returned within 7 days if unused and in original packaging</span>
                  </div>
                </div>
              </div>

              <div className="mt-6 bg-amber-50 border border-amber-200 rounded-lg p-4">
                <div className="flex items-start gap-3">
                  <AlertCircle className="w-5 h-5 text-amber-600 mt-0.5 flex-shrink-0" />
                  <div>
                    <p className="font-medium text-amber-800">Note on Customized Products</p>
                    <p className="text-sm text-amber-700 mt-1">
                      Customized or personalized items (with logos, names, or special branding) are non-refundable once production has started.
                    </p>
                  </div>
                </div>
              </div>
            </div>

            {/* Section 4: Token/Advance Payments */}
            <div className="p-8 border-b border-gray-100">
              <h2 className="text-2xl font-bold text-navy mb-4 flex items-center gap-3">
                <span className="w-8 h-8 bg-navy text-white rounded-full flex items-center justify-center text-sm">4</span>
                Token & Advance Payments
              </h2>
              <p className="text-slate-700 mb-4">
                For bookings that require a token amount (typically 20% of total booking value):
              </p>
              <ul className="space-y-3 text-slate-700">
                <li className="flex items-start gap-3">
                  <CheckCircle className="w-5 h-5 text-navy mt-0.5 flex-shrink-0" />
                  Token amounts are adjustable against the final invoice
                </li>
                <li className="flex items-start gap-3">
                  <CheckCircle className="w-5 h-5 text-navy mt-0.5 flex-shrink-0" />
                  Refund of token follows the same cancellation policy timeline mentioned above
                </li>
                <li className="flex items-start gap-3">
                  <CheckCircle className="w-5 h-5 text-navy mt-0.5 flex-shrink-0" />
                  Token is fully refundable if venue confirms unavailability within 24 hours
                </li>
              </ul>
            </div>

            {/* Section 5: Refund Process */}
            <div className="p-8 border-b border-gray-100">
              <h2 className="text-2xl font-bold text-navy mb-4 flex items-center gap-3">
                <span className="w-8 h-8 bg-navy text-white rounded-full flex items-center justify-center text-sm">5</span>
                Refund Process
              </h2>
              
              <div className="space-y-4">
                <div className="flex items-start gap-4">
                  <div className="w-8 h-8 bg-gold text-navy rounded-full flex items-center justify-center font-bold flex-shrink-0">1</div>
                  <div>
                    <h4 className="font-semibold text-navy">Submit Request</h4>
                    <p className="text-slate-800 text-sm">Contact our support team via email or WhatsApp with your booking ID and reason for cancellation</p>
                  </div>
                </div>
                <div className="flex items-start gap-4">
                  <div className="w-8 h-8 bg-gold text-navy rounded-full flex items-center justify-center font-bold flex-shrink-0">2</div>
                  <div>
                    <h4 className="font-semibold text-navy">Review (24-48 hours)</h4>
                    <p className="text-slate-800 text-sm">Our team will review your request and confirm the applicable refund amount</p>
                  </div>
                </div>
                <div className="flex items-start gap-4">
                  <div className="w-8 h-8 bg-gold text-navy rounded-full flex items-center justify-center font-bold flex-shrink-0">3</div>
                  <div>
                    <h4 className="font-semibold text-navy">Approval</h4>
                    <p className="text-slate-800 text-sm">Upon approval, refund will be initiated through Razorpay</p>
                  </div>
                </div>
                <div className="flex items-start gap-4">
                  <div className="w-8 h-8 bg-gold text-navy rounded-full flex items-center justify-center font-bold flex-shrink-0">4</div>
                  <div>
                    <h4 className="font-semibold text-navy">Credit (5-7 Business Days)</h4>
                    <p className="text-slate-800 text-sm">Refund will be credited to the original payment method within 5-7 business days</p>
                  </div>
                </div>
              </div>

              <div className="mt-6 bg-blue-50 border border-blue-200 rounded-lg p-4">
                <p className="text-sm text-blue-800">
                  <strong>Note:</strong> Refund processing time may vary depending on your bank. Credit card refunds may take 
                  up to 2 billing cycles to reflect in your statement.
                </p>
              </div>
            </div>

            {/* Section 6: Non-Refundable Items */}
            <div className="p-8 border-b border-gray-100">
              <h2 className="text-2xl font-bold text-navy mb-4 flex items-center gap-3">
                <span className="w-8 h-8 bg-navy text-white rounded-full flex items-center justify-center text-sm">6</span>
                Non-Refundable Items
              </h2>
              <p className="text-slate-700 mb-4">The following are not eligible for refund:</p>
              <ul className="space-y-2 text-slate-700">
                <li className="flex items-start gap-3">
                  <AlertCircle className="w-5 h-5 text-red-500 mt-0.5 flex-shrink-0" />
                  Service fees and platform charges
                </li>
                <li className="flex items-start gap-3">
                  <AlertCircle className="w-5 h-5 text-red-500 mt-0.5 flex-shrink-0" />
                  Customized products after production has started
                </li>
                <li className="flex items-start gap-3">
                  <AlertCircle className="w-5 h-5 text-red-500 mt-0.5 flex-shrink-0" />
                  No-show bookings (failure to attend without prior cancellation)
                </li>
                <li className="flex items-start gap-3">
                  <AlertCircle className="w-5 h-5 text-red-500 mt-0.5 flex-shrink-0" />
                  Services already rendered or consumed
                </li>
                <li className="flex items-start gap-3">
                  <AlertCircle className="w-5 h-5 text-red-500 mt-0.5 flex-shrink-0" />
                  Event permit application fees (government fees are non-refundable)
                </li>
              </ul>
            </div>

            {/* Section 7: Contact */}
            <div className="p-8 bg-slate-50">
              <h2 className="text-2xl font-bold text-navy mb-4 flex items-center gap-3">
                <span className="w-8 h-8 bg-navy text-white rounded-full flex items-center justify-center text-sm">7</span>
                Contact Us for Refunds
              </h2>
              <p className="text-slate-700 mb-6">
                For refund requests or any queries related to this policy, please contact us:
              </p>
              <div className="grid md:grid-cols-2 gap-4">
                <a href="mailto:refunds@mycorporatehub.com" className="flex items-center gap-3 bg-white p-4 rounded-lg border border-gray-200 hover:border-gold transition-colors">
                  <Mail className="w-6 h-6 text-gold" />
                  <div>
                    <p className="text-sm text-slate-700">Email</p>
                    <p className="font-medium text-navy">refunds@mycorporatehub.com</p>
                  </div>
                </a>
                <a href="https://wa.me/919664870766" target="_blank" rel="noopener noreferrer" className="flex items-center gap-3 bg-white p-4 rounded-lg border border-gray-200 hover:border-gold transition-colors">
                  <Phone className="w-6 h-6 text-gold" />
                  <div>
                    <p className="text-sm text-slate-700">WhatsApp</p>
                    <p className="font-medium text-navy">+91 96648 70766</p>
                  </div>
                </a>
              </div>
              
              <div className="mt-6 text-center">
                <p className="text-sm text-slate-700">
                  Business Hours: Monday - Saturday, 10:00 AM - 7:00 PM IST
                </p>
              </div>
            </div>
          </div>

          {/* Payment Partner */}
          <div className="mt-12 text-center">
            <p className="text-sm text-slate-900 font-medium mb-4">Secure Payments Powered By</p>
            <div className="flex items-center justify-center gap-4 bg-white px-8 py-4 rounded-xl border border-gray-200 inline-flex">
              <img 
                src="https://cdn.razorpay.com/logo.svg" 
                alt="Razorpay" 
                className="h-6"
              />
              <div className="w-px h-8 bg-gray-300"></div>
              <img 
                src="https://upload.wikimedia.org/wikipedia/commons/5/5e/Visa_Inc._logo.svg" 
                alt="Visa" 
                className="h-5"
              />
              <img 
                src="https://upload.wikimedia.org/wikipedia/commons/2/2a/Mastercard-logo.svg" 
                alt="Mastercard" 
                className="h-8"
              />
              <div className="w-px h-8 bg-gray-300"></div>
              <img 
                src="https://badges.razorpay.com/badge-dark.png" 
                alt="Razorpay Trusted Business" 
                className="h-8"
              />
            </div>
          </div>

        </div>
      </section>

      {/* Footer */}
      <footer className="bg-navy-900 text-white py-8">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <p className="text-slate-700 text-sm">
            © 2025 My Corporate Hub. All rights reserved.
          </p>
          <div className="flex justify-center gap-6 mt-4">
            <Link to="/privacy-policy" className="text-slate-700 hover:text-white text-sm transition-colors">Privacy Policy</Link>
            <Link to="/terms-conditions" className="text-slate-700 hover:text-white text-sm transition-colors">Terms & Conditions</Link>
            <Link to="/" className="text-slate-700 hover:text-white text-sm transition-colors">Home</Link>
          </div>
        </div>
      </footer>
    </div>
  );
}
